'use client';

import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { createClient } from '@/lib/supabase/client';

const AuthContext = createContext<any>({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const supabase = createClient();

  // Prevent concurrent/duplicate profile fetches
  const fetchingProfileRef = useRef(false);
  const lastFetchedUserIdRef = useRef<string | null>(null);

  const fetchProfile = async (userId: string) => {
    // Skip if already fetching or already fetched for this user
    if (fetchingProfileRef.current || lastFetchedUserIdRef.current === userId) return;
    fetchingProfileRef.current = true;
    try {
      const { data } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();
      setProfile(data ?? null);
      lastFetchedUserIdRef.current = userId;
    } catch {
      setProfile(null);
    } finally {
      fetchingProfileRef.current = false;
    }
  };

  // Force a fresh profile fetch, bypassing the cache (used after Stripe checkout)
  const refreshProfile = async () => {
    if (!user) return;
    fetchingProfileRef.current = false;
    lastFetchedUserIdRef.current = null;
    await fetchProfile(user.id);
  };

  useEffect(() => {
    let initialSessionHandled = false;

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      initialSessionHandled = true;
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) fetchProfile(session.user.id);
      setLoading(false);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      // Avoid double-processing the initial session
      if (!initialSessionHandled && session) return;

      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        // Reset cache when user changes
        if (lastFetchedUserIdRef.current !== session.user.id) {
          lastFetchedUserIdRef.current = null;
        }
        fetchProfile(session.user.id);
      } else {
        setProfile(null);
        lastFetchedUserIdRef.current = null;
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Email/Password Sign Up
  const signUp = async (email: string, password: string, metadata: Record<string, string> = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: metadata?.fullName || '',
          business_name: metadata?.businessName || '',
          avatar_url: metadata?.avatarUrl || '',
        },
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    if (error) throw error;
    return data;
  };

  // Email/Password Sign In
  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
    return data;
  };

  // Sign Out
  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    lastFetchedUserIdRef.current = null;
  };

  // Get Current User
  const getCurrentUser = async () => {
    const {
      data: { user },
      error,
    } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  // Check if Email is Verified
  const isEmailVerified = () => {
    return user?.email_confirmed_at !== null;
  };

  // Get User Profile from Database
  const getUserProfile = async () => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    if (error) throw error;
    return data;
  };

  const value = {
    user,
    session,
    profile,
    loading,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
